import { api } from '../../lib/api';
import { putToStorage } from '../../lib/storage';

export const ITEM_STATUSES = [
  { value: 'Draft', label: 'Draft', tone: 'neutral' },
  { value: 'Active', label: 'Active', tone: 'success' },
  { value: 'Inactive', label: 'Inactive', tone: 'warning' },
  { value: 'Archived', label: 'Archived', tone: 'danger' },
];

export const itemStatusMeta = (status) =>
  ITEM_STATUSES.find((s) => s.value === status) || { value: status, label: status, tone: 'neutral' };

export const UNIT_DIMENSIONS = [
  { value: 'Count', label: 'Count' },
  { value: 'Weight', label: 'Weight' },
  { value: 'Volume', label: 'Volume' },
  { value: 'Length', label: 'Length' },
  { value: 'Other', label: 'Other' },
];

function toQuery(params) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.append(k, v);
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

export const catalogApi = {
  // Items
  listItems: (params = {}) => api.get(`/items${toQuery(params)}`),
  getItem: (id) => api.get(`/items/${id}`),
  createItem: (payload) => api.post('/items', payload),
  updateItem: (id, payload) => api.putWithId(`/items/${id}`, id, payload),
  removeItem: (id) => api.del(`/items/${id}`),

  // Item analytics (pricing intelligence + purchase summary)
  itemStats: (id) => api.get(`/items/${id}/stats`),

  // Catalog-wide purchase-price movements over a date window ({ from, to } as ISO strings)
  priceMovements: (params = {}) => api.get(`/items/price-movements${toQuery(params)}`),

  // Item image (direct-to-S3 via presigned URL)
  getItemImageUrl: (id) => api.get(`/items/${id}/image`),
  removeItemImage: (id) => api.del(`/items/${id}/image`),
  uploadItemImage: async (id, file) => {
    const contentType = file.type || 'application/octet-stream';
    const ticket = await api.post(`/items/${id}/image/upload-url`,
      { fileName: file.name, contentType, sizeBytes: file.size });
    await putToStorage(ticket.uploadUrl, file, contentType);
    return api.put(`/items/${id}/image`, { imageKey: ticket.storageKey });
  },

  // Purchase history
  listPurchases: (itemId) => api.get(`/items/${itemId}/purchases`),
  addPurchase: (itemId, payload) => api.post(`/items/${itemId}/purchases`, payload),
  removePurchase: (itemId, purchaseId) => api.del(`/items/${itemId}/purchases/${purchaseId}`),

  // Item ↔ supplier price list
  listItemSuppliers: (itemId) => api.get(`/items/${itemId}/suppliers`),
  addItemSupplier: (itemId, payload) => api.post(`/items/${itemId}/suppliers`, payload),
  updateItemSupplier: (itemId, linkId, payload) => api.put(`/items/${itemId}/suppliers/${linkId}`, payload),
  removeItemSupplier: (itemId, linkId) => api.del(`/items/${itemId}/suppliers/${linkId}`),

  // Supplier-side price list
  listSupplierCatalog: (supplierId) => api.get(`/suppliers/${supplierId}/catalog`),
  // Add an item to a supplier's price list — links an existing item, or creates a new one when `newItem` is provided.
  addSupplierCatalogItem: (supplierId, payload) => api.post(`/suppliers/${supplierId}/catalog`, payload),

  // Units of measure
  listUnits: () => api.get('/catalog/units'),
  createUnit: (payload) => api.post('/catalog/units', payload),
  updateUnit: (id, payload) => api.putWithId(`/catalog/units/${id}`, id, payload),
  removeUnit: (id) => api.del(`/catalog/units/${id}`),

  // Categories
  listCategories: () => api.get('/catalog/categories'),
  createCategory: (payload) => api.post('/catalog/categories', payload),
  updateCategory: (id, payload) => api.putWithId(`/catalog/categories/${id}`, id, payload),
  removeCategory: (id) => api.del(`/catalog/categories/${id}`),

  // Locations (shared with Settings; par levels are location-scoped)
  listLocations: (propertyId) => api.get(`/locations${propertyId ? `?propertyId=${propertyId}` : ''}`),
  createLocation: (payload) => api.post('/locations', payload),
  updateLocation: (id, payload) => api.putWithId(`/locations/${id}`, id, payload),
  removeLocation: (id) => api.del(`/locations/${id}`),
};

export const LOCATION_KINDS = [
  { value: 'store', label: 'Store room' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'bar', label: 'Bar' },
  { value: 'outlet', label: 'Outlet' },
  { value: 'cellar', label: 'Cellar' },
  { value: 'other', label: 'Other' },
];
